import React from 'react';
import PropTypes from 'prop-types';
import { InfoWindow, OverlayView } from '@react-google-maps/api';

import LocationNameDisplay from 'components/LocationNameDisplay';
import Button from 'components/Button';
import LinkButton from 'components/LinkButton';
import { sanitizeValue, sanitizeUrl } from 'utils/sanitize';
import { openUrl } from 'utils/window';

import { MarkerContainer, MarkerContent } from 'styles/styledComponents';

export default class MarkerComponent extends React.Component {
  markerRef = React.createRef();

  handleMoreInfo = () => {
    const { location } = this.props;
    openUrl(sanitizeUrl(location.url));
  };

  getGoogleMapsUrl = () => {
    const { location } = this.props;
    const googleMapsUri = 'https://www.google.com/maps/search/?api=1';
    let searchUrl = '';
    const lat = parseFloat(location.lat);
    const lng = parseFloat(location.lng);
    if (location.google_place_id) {
      searchUrl = `${googleMapsUri}&query=${lat},${lng}&query_place_id=${location.google_place_id}`;
    } else {
      const urlSafeAddress = encodeURI(location.address);
      searchUrl = `${googleMapsUri}&query=${urlSafeAddress}`;
    }
    return searchUrl;
  };

  getMarkerProps = () => {
    const { location, markerIcon, handleToggleOpen } = this.props;
    const markerProps = {
      position: {
        lat: parseFloat(location.lat),
        lng: parseFloat(location.lng)
      },
      onClick: () => handleToggleOpen(location.id),
      title: sanitizeValue(location.name)
    };
    if (markerIcon !== 'https://getbento.imgix.net/' && markerIcon) {
      markerProps.icon = markerIcon;
    }
    return markerProps;
  };

  displayCustomButton = () => {
    const { location } = this.props;
    return (
      location.store_locator_custom_button_text &&
      location.store_locator_custom_button_url
    );
  };

  render() {
    const {
      activeId,
      handleToggleClose,
      location,
      displayMoreInfo,
      markerIcon,
      handleToggleOpen,
      map
    } = this.props;
    const sanitizedPhoneNumber = sanitizeValue(location.phone_number);
    const sanitizedName = sanitizeValue(location.name);
    const sanitizedUrl = sanitizeUrl(location.url);
    const sanitizedCustomButtonText = sanitizeValue(
      location.store_locator_custom_button_text
    );
    const sanitizedCustomButtonUrl = sanitizeValue(
      location.store_locator_custom_button_url
    );
    const markerPosition = {
      lat: parseFloat(location.lat),
      lng: parseFloat(location.lng)
    };

    // Center map on marker when pin is clicked and InfoWindow opens
    const handlePinClick = id => {
      if (map && typeof map.panTo === 'function') {
        map.panTo(markerPosition);
      }
      handleToggleOpen(id);
    };
    const markerHeight = 90; // pixels
    const markerWidth = 70; // pixels

    return (
      <>
        <OverlayView
          position={markerPosition}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          getPixelPositionOffset={(width, height) => ({ x: 0, y: 0 })}
        >
          <div
            role="button"
            tabIndex={0}
            aria-label={`Show details for ${location.name}`}
            onClick={() => handlePinClick(location.id)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                handlePinClick(location.id);
              }
            }}
            style={{
              cursor: 'pointer',
              width: `${markerWidth}px`,
              height: `${markerHeight}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'absolute',
              transform: 'translate(-50%, -100%)', // top-center anchor
              willChange: 'transform'
            }}
          >
            {markerIcon && markerIcon !== 'https://getbento.imgix.net/' ? (
              <img
                src={markerIcon}
                alt={location.name}
                width={markerWidth}
                height={markerHeight}
                style={{ objectFit: 'contain' }}
              />
            ) : (
              <div
                style={{
                  background: '#4285F4',
                  borderRadius: '50%',
                  border: '2px solid #fff',
                  width: `${markerWidth}px`,
                  height: `${markerHeight}px`
                }}
              />
            )}
          </div>
        </OverlayView>
        {activeId === location.id && (
          <InfoWindow
            onCloseClick={handleToggleClose}
            position={markerPosition}
            options={{
              pixelOffset: new window.google.maps.Size(0, -markerHeight)
            }}
          >
            <MarkerContainer id="infoWindow">
              <LocationNameDisplay
                id="infoWindow-title"
                name={sanitizedName}
                url={sanitizedUrl}
              />
              {location.address && (
                <MarkerContent
                  href={this.getGoogleMapsUrl()}
                  target="_blank"
                  id="infoWindow-address"
                  rel="noreferrer"
                >
                  {location.street}
                  <br id="infoWindow-address-break" />
                  {location.city}, {location.state} {location.postal_code}
                </MarkerContent>
              )}
              <br id="infoWindow-break" />
              {location.phone_number && (
                <React.Fragment>
                  <MarkerContent
                    href={`tel:${sanitizedPhoneNumber}`}
                    id="infoWindow-phone"
                  >
                    {sanitizedPhoneNumber}
                  </MarkerContent>
                  <br id="infoWindow-break" />
                </React.Fragment>
              )}
              {displayMoreInfo && (
                <Button
                  label="Location Details"
                  handleClick={this.handleMoreInfo}
                  id="infoWindow-moreInfo"
                  addTopMargin
                />
              )}
              {this.displayCustomButton() && (
                <LinkButton
                  className="locationListItem-customButton locationListItem-link"
                  label={sanitizedCustomButtonText}
                  addTopMargin
                  href={sanitizedCustomButtonUrl}
                  isExternal
                />
              )}
            </MarkerContainer>
          </InfoWindow>
        )}
      </>
    );
  }
}

MarkerComponent.propTypes = {
  activeId: PropTypes.number,
  displayMoreInfo: PropTypes.bool.isRequired,
  handleToggleClose: PropTypes.func.isRequired,
  handleToggleOpen: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  markerIcon: PropTypes.string
};
